import Stop from "@mui/icons-material/Stop";
import { Stack, Typography } from "@mui/material";
import { type ChangeEvent, type SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";

import CoAppMultiSelect from "@/components/global/components/inputs/CoAppMultiSelect";
import type { Tag } from "@/components/tags/types";

import type { StopRuleNode } from "../types/nodes";
import { mergeStopTagNames, normalizeStopTagNames } from "../utils/stop-tag-utils";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface StopFormState {
	stopName: string;
	comment: string;
	tags: string[];
}

export type StopFormDraftPatch = Partial<Pick<StopRuleNode["data"], "name" | "comment" | "tags">>;

export interface StopFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: StopFormState, shouldClose: boolean) => void;
	onDraftPatch?: (patch: StopFormDraftPatch) => void;
	activeNodeId?: string | null;
	initialState?: Partial<StopFormState>;
	isSaving?: boolean;
	getDuplicateNameError?: (name: string) => string | undefined;
	stopTagsInOrganization?: Tag[];
	/** Tags already configured on other stop nodes in this rule (unsaved to org API). */
	ruleStopTagNames?: string[];
}

const StopForm = (props: StopFormProps) => {
	const {
		open,
		onClose,
		onSave,
		onDraftPatch,
		activeNodeId = null,
		initialState,
		isSaving = false,
		getDuplicateNameError,
		stopTagsInOrganization = [],
		ruleStopTagNames = [],
	} = props;

	const buildInitialState = (): StopFormState => ({
		stopName: initialState?.stopName ?? "",
		comment: initialState?.comment ?? "",
		tags: normalizeStopTagNames(initialState?.tags),
	});

	const initialFormState = useMemo(buildInitialState, [initialState]);
	const initialFormStateRef = useRef(initialFormState);
	initialFormStateRef.current = initialFormState;

	const prevSyncKeyRef = useRef("");

	const [formState, setFormState] = useState<StopFormState>(initialFormState);

	useEffect(() => {
		const syncKey = `${open}-${activeNodeId ?? ""}`;
		if (open && syncKey !== prevSyncKeyRef.current) {
			setFormState(initialFormStateRef.current);
			prevSyncKeyRef.current = syncKey;
		}
		if (!open) {
			prevSyncKeyRef.current = "";
		}
	}, [open, activeNodeId]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(formState.stopName),
		[getDuplicateNameError, formState.stopName],
	);

	const isSaveDisabled = !formState.stopName.trim() || Boolean(duplicateNameError);

	const handleTagsChange = (_event: SyntheticEvent<Element, Event>, newValue: unknown[]) => {
		const normalizedTags = normalizeStopTagNames(newValue);
		const lastValue = normalizedTags.length > 0 ? normalizedTags[normalizedTags.length - 1] : undefined;
		if (lastValue && formState.tags.includes(lastValue) && normalizedTags.length > formState.tags.length) {
			return;
		}
		setFormState((previous) => ({
			...previous,
			tags: normalizedTags,
		}));
		onDraftPatch?.({ tags: normalizedTags });
	};

	const handleStopNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const stopName = event.target.value;
		setFormState((previous) => ({
			...previous,
			stopName,
		}));

		const trimmed = stopName.trim();
		if (!trimmed || getDuplicateNameError?.(stopName)) {
			return;
		}
		onDraftPatch?.({ name: trimmed });
	};

	const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
		const comment = event.target.value;
		setFormState((previous) => ({
			...previous,
			comment,
		}));
		onDraftPatch?.({ comment });
	};

	const handleSave = () => {
		onSave(
			{
				stopName: formState.stopName.trim(),
				comment: formState.comment,
				tags: formState.tags,
			},
			true,
		);
	};

	const stopTagOptions = useMemo(
		() => mergeStopTagNames(stopTagsInOrganization, ruleStopTagNames),
		[stopTagsInOrganization, ruleStopTagNames],
	);

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Stop"
			icon={<Stop />}
			iconColor="#D32E26"
			gradient="linear-gradient(135deg, #E94136 0%, #B81E15 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Stop"
		>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					label="Name"
					value={formState.stopName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={handleStopNameChange}
					width="100%"
				/>
				<CanvasFormTextField
					fullWidth
					label="Comment"
					value={formState.comment}
					multiline
					minRows={3}
					onChange={handleCommentChange}
					width="100%"
					sx={{
						"& .MuiInputBase-inputMultiline": {
							resize: "vertical",
						},
					}}
				/>
				<Stack spacing={1}>
					<CoAppMultiSelect
						isFreeSolo
						label="Tags"
						onChange={handleTagsChange}
						options={stopTagOptions}
						textFieldWidth="100%"
						value={formState.tags}
						customRenderOptionLabel={(option) =>
							typeof option === "string" ? option : (option as { name?: string })?.name ?? ""
						}
					/>
					<Typography variant={"body3" as any}>
						Select from existing tags or create your own by typing and pressing &apos;ENTER&apos; when finished.
					</Typography>
				</Stack>
			</Stack>
		</BaseForm>
	);
};

export default StopForm;
